import { useMemo, useState } from 'react';
import { Plus, Users, Trash2, Pencil, Check, X, Search } from 'lucide-react';
import type { WorkoutGroup, MemberSummary } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Modal, Badge, Skeleton, EmptyState, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { useAsync } from '../lib/useAsync';
import { getWorkoutGroups, createWorkoutGroup, updateWorkoutGroup, deleteWorkoutGroup, listMembers } from '../lib/gymApi';

/** Member groups (rosters) for targeting workouts/sessions. */
export function WorkoutGroupsPage() {
  const { data, loading, reload } = useAsync(getWorkoutGroups);
  const { data: membersPage } = useAsync(() => listMembers({ size: 1000 }));
  const members = useMemo(() => membersPage?.items ?? [], [membersPage]);
  const [editing, setEditing] = useState<WorkoutGroup | 'new' | null>(null);
  const groups = data ?? [];

  return (
    <section>
      <PageHeader
        eyebrow={data ? `${groups.length} groups` : undefined}
        title="Groups"
        description="Rosters of members — target a workout or open session at a whole group at once."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">New group</span>
          </Button>
        }
      />

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-card" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<Users className="size-6" aria-hidden />}
          title="No groups yet"
          description="Create a group to schedule a workout for several members at once."
          action={<Button onClick={() => setEditing('new')}>New group</Button>}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <Card key={g.id} className="flex flex-col p-5">
              <div className="flex items-start justify-between">
                <p className="font-display text-lg font-semibold text-ink">{g.name}</p>
                <button type="button" aria-label={`Edit ${g.name}`} onClick={() => setEditing(g)} className="text-muted hover:text-ink">
                  <Pencil className="size-4" aria-hidden />
                </button>
              </div>
              <p className="mt-0.5 text-sm text-muted">{g.memberCount} {g.memberCount === 1 ? 'member' : 'members'}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {g.members.slice(0, 6).map((m) => (
                  <Badge key={m.id}>{m.name}</Badge>
                ))}
                {g.memberCount > 6 && <span className="text-xs text-muted">+{g.memberCount - 6}</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <GroupForm
          group={editing === 'new' ? null : editing}
          members={members}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </section>
  );
}

function GroupForm({
  group,
  members,
  onClose,
  onSaved,
}: {
  group: WorkoutGroup | null;
  members: MemberSummary[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const confirm = useConfirm();
  const [name, setName] = useState(group?.name ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set(group?.members.map((m) => m.id) ?? []));
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = saving || deleting;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return members.filter((m) => !needle || m.fullName.toLowerCase().includes(needle)).slice(0, 100);
  }, [members, q]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    setError(null);
    if (!name.trim()) {
      setError('Give the group a name.');
      return;
    }
    setSaving(true);
    const req = { name: name.trim(), memberIds: Array.from(selected) };
    try {
      if (group) await updateWorkoutGroup(group.id, req);
      else await createWorkoutGroup(req);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the group.');
      setSaving(false);
    }
  }

  async function remove() {
    if (!group) return;
    const ok = await confirm({ title: `Delete "${group.name}"?`, description: "This can't be undone.", confirmLabel: 'Delete', tone: 'danger' });
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteWorkoutGroup(group.id);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not delete the group.');
      setDeleting(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex max-h-[80vh] w-full flex-col">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">{group ? 'Edit group' : 'New group'}</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div className="mt-3">
          <Input label="Group name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Beginners" />
        </div>

        <p className="mt-4 text-sm font-medium text-ink">Members ({selected.size})</p>
        <div className="relative mt-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search members…"
            className="h-touch w-full rounded-control border border-line bg-elevated pl-9 pr-3 text-sm text-ink outline-none focus:border-ink"
          />
        </div>

        <div className="mt-2 min-h-0 flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">No members.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {filtered.map((m) => {
                const on = selected.has(m.id);
                return (
                  <li key={m.id}>
                    <button type="button" onClick={() => toggle(m.id)} className="flex w-full items-center gap-3 py-2.5 text-left">
                      <span className={cn('flex size-5 items-center justify-center rounded-md border', on ? 'border-ink bg-ink text-on-dark' : 'border-line-strong')}>
                        {on && <Check className="size-3.5" aria-hidden />}
                      </span>
                      <span className="text-sm text-ink">{m.fullName}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {error && (
          <p role="alert" className="mt-3 rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
            {error}
          </p>
        )}

        <div className="mt-3 flex items-center justify-end gap-2 border-t border-line pt-3">
          {group && (
            <Button size="sm" variant="ghost" onClick={remove} loading={deleting} disabled={busy} className="mr-auto text-bad-ink hover:bg-bad-soft">
              {!deleting && <Trash2 className="size-4" aria-hidden />} Delete
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} loading={saving} disabled={busy}>
            {!saving && <Check className="size-4" aria-hidden />} Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
